import React from "react";
import { Button, Col, Container, Row } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { Map } from "@vis.gl/react-google-maps";
import { MapEvent } from "@vis.gl/react-google-maps/dist/components/map/use-map-events";
// @vis.gl/react-google-maps doesn't include a marker clusterer, so we use the official Google Maps MarkerClusterer.
import { Cluster, MarkerClusterer } from "@googlemaps/markerclusterer";
import { Point } from "geojson";
import * as turf from "@turf/turf";
import { ModuleRegistry, themeBalham } from "ag-grid-community";
import { AllEnterpriseModule, ColGroupDef } from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import type { ColDef } from "ag-grid-community";
import { AG_GRID_LOCALE_NL } from "@ag-grid-community/locale";
import type {
  RowClickedEvent,
  RowGroupOpenedEvent,
} from "ag-grid-community/dist/types/src/events";

import NavBar from "../common/components/NavBar";
import LoadingIndicator from "../common/components/LoadingIndicator";
import { routes } from "../../Router";
import { generateExcelForSelection, getLeadSearch } from "../../api/LeadSearch";
import { getIdealZoomLevel } from "../components/google-maps/utils";
import config from "../../config";
import { getBagObjectPerceel, getBagObjects } from "../../api/RealEstate";
import LeadDetailCell from "./LeadDetailCell/LeadDetailCell";
import { RowFocusTracker } from "./rowFocus";

// Register ag grid community modules
// (see https://www.ag-grid.com/react-data-grid/getting-started/#register-modules).
ModuleRegistry.registerModules([AllEnterpriseModule]);

export default function LeadSearchDetail(): React.JSX.Element {
  /**
   * Lead search detail screen.
   */
  const navigate = useNavigate();

  const { leadSearchId } = useParams();

  const mapRef = React.useRef<google.maps.Map | null>(null);
  const gridRef = React.useRef<AgGridReact | null>(null);
  const markerClustererRef = React.useRef<MarkerClusterer | null>(null);
  const markersRef = React.useRef<IBagMarker[]>([]);
  const perceelPolygonRef = React.useRef<google.maps.Polygon | null>(null);
  const rowFocusTrackerRef = React.useRef<RowFocusTracker | null>(null);
  if (rowFocusTrackerRef.current === null) {
    rowFocusTrackerRef.current = new RowFocusTracker();
  }
  const rowFocusTracker = rowFocusTrackerRef.current;

  const [isLoading, setIsLoading] = React.useState<boolean>(true);
  const [isExportingSelection, setIsExportingSelection] =
    React.useState<boolean>(false);
  const [leadSearch, setLeadSearch] = React.useState<ILeadSearch>();
  const [bagObjects, setBagObjects] = React.useState<IBagObject[]>([]);
  const [selectedBagObjects, setSelectedBagObjects] = React.useState<
    IBagObject[]
  >([]);
  const [focusedBagObject, setFocusedBagObjectId] =
    React.useState<IBagObject | null>(null);

  const [mapDefaultCenter, setMapDefaultCenter] = React.useState<Point>(
    config.headquartersLocation,
  );
  const [mapDefaultZoom, setMapDefaultZoom] = React.useState<number>(8);
  const [mapIsReady, setMapIsReady] = React.useState<boolean>(false);
  const [plottedBagObjects, setPlottedBagObjects] = React.useState<
    IBagObject[]
  >([]);

  const highlightMarker = (bagObject: IBagObject) => {
    /**
     * Highlight the marker of the given BAG object.
     * This is done by changing the marker's color to dark blue.
     */
    markersRef.current.forEach((marker) => {
      if (marker.bagObjectObjectId === bagObject.objectId) {
        // Highlight the marker of the given BAG object.
        marker.content = new google.maps.marker.PinElement({
          background: "#213b5e",
          borderColor: "#ffffff",
          glyphColor: "#ffffff",
          scale: 1.5,
        }).element;
      } else {
        // Reset the marker's content if it is not the one we want to highlight.
        marker.content = new google.maps.marker.PinElement({
          background: "#e50051",
          borderColor: "#ffffff",
          glyphColor: "#ffffff",
          scale: 1.25,
        }).element;
      }
    });
  };

  const panToMarker = (bagObject: IBagObject, autoZoom: boolean = true) => {
    /**
     * Pan the map to the marker of the BAG object.
     */
    const marker = markersRef.current.find(
      (m) => (m as IBagMarker).bagObjectObjectId === bagObject.objectId,
    );
    const pos = marker?.position;
    if (pos && mapRef.current) {
      mapRef.current.panTo(pos);
      if (autoZoom) {
        mapRef.current.setZoom(Math.max(mapRef.current.getZoom() || 0, 16));
      }
      highlightMarker(bagObject);
    }
  };

  const focusOnRow = (
    bagObject: IBagObject,
    ensureNodeVisible: boolean = false,
  ) => {
    /**
     * Focus the grid on the row of the BAG object.
     * Collapse the previously expanded row (only one detail view is shown at a
     * time), expand this row, then scroll the grid to it and focus on it.
     * The tracker does this in O(1) instead of walking every row node.
     */
    const api = gridRef.current?.api;
    if (api && rowFocusTracker.focus(api, bagObject.id, ensureNodeVisible)) {
      setFocusedBagObjectId(bagObject);
    }
  };

  const handleRowGroupOpened = (event: RowGroupOpenedEvent) => {
    /**
     * Keep the single-expanded-row invariant for rows expanded via the
     * group-cell chevron or the Enter key, which bypass focusOnRow.
     */
    rowFocusTracker.onRowGroupOpened(event.api, event.node, event.expanded);
  };

  const handleRowClicked = (event: RowClickedEvent) => {
    /**
     * Handle clicking a BAG object's (master) row in the grid.
     * Scroll the grid to the relevant row and pan the map to the relevant marker.
     */
    if (event.node.master) {
      const bagObject = event.data;
      focusOnRow(bagObject, true);
      // Sleep for a short time to ensure the detail row is rendered before focusing on it.
      setTimeout(() => focusOnRow(bagObject, true), 50);
      panToMarker(bagObject, true);
    }
  };

  const handleMarkerClicked = (bagObject: IBagObject) => {
    /**
     * Handle clicking a BAG object's marker on the map.
     * Scroll the grid to the relevant row and pan the map to the relevant marker.
     */
    panToMarker(bagObject, false);
    focusOnRow(bagObject, true);
    // Sleep for a short time to ensure the detail row is rendered before focusing on it.
    setTimeout(() => focusOnRow(bagObject, true), 50);
  };

  React.useEffect(() => {
    /**
     * Fetch lead search data and BAG objects,
     * and set the map's default center and zoom level.
     */
    if (leadSearchId) {
      // Get lead search data.
      getLeadSearch(leadSearchId)
        .then(async (_leadSearch: ILeadSearch) => {
          setLeadSearch(_leadSearch);
          // Get BAG objects for the lead search.
          await getBagObjects(leadSearchId).then((_bagObjects: IBagObject[]) =>
            setBagObjects(_bagObjects),
          );
          // Set the map default center and zoom level based on the focus area polygon.
          setMapDefaultCenter(
            turf.centroid(_leadSearch.focusAreaPolygon).geometry,
          );
          setMapDefaultZoom(getIdealZoomLevel(_leadSearch.focusAreaPolygon));
          setIsLoading(false);
        })
        .catch((error: IApiError) =>
          navigate(routes.error(error.statusCode || 404), { replace: true }),
        );
    }
  }, [leadSearchId, navigate]);

  React.useEffect(
    () => {
      /**
       * Plot BAG objects on the map.
       */
      if (mapIsReady && mapRef?.current) {
        // Clear existing markers, if any.
        markerClustererRef?.current?.clearMarkers();
        // Create markers for each BAG object.
        const markers = plottedBagObjects.map((bagObject) => {
          // @vis.gl/react-google-maps isn't compatible with the official Google Maps MarkerClusterer,
          // so we use the official Google Maps AdvancedMarkerElement.
          const marker: IBagMarker =
            new google.maps.marker.AdvancedMarkerElement({
              title: bagObject.addressFormatted,
              position: {
                lat: bagObject.centroid.coordinates[1],
                lng: bagObject.centroid.coordinates[0],
              },
              content: new google.maps.marker.PinElement({
                background: "#e50051",
                borderColor: "#ffffff",
                glyphColor: "#ffffff",
                scale: 1.25,
              }).element,
            });
          // Add BAG object ID to the marker for later reference.
          marker.bagObjectObjectId = bagObject.objectId;
          marker.addListener("click", () => handleMarkerClicked(bagObject));
          return marker;
        });
        markersRef.current = markers;
        // Initialize/update the marker cluster.
        markerClustererRef.current = new MarkerClusterer({
          map: mapRef.current,
          markers: markers,
          algorithmOptions: { maxZoom: 13 },
          renderer: {
            render: (cluster: Cluster) => {
              return new google.maps.marker.AdvancedMarkerElement({
                title: `${cluster.count} objecten`,
                position: cluster.position,
                content: new google.maps.marker.PinElement({
                  background: "#e50051",
                  borderColor: "#ffffff",
                  glyphColor: "#ffffff",
                  scale: 1.25,
                  glyph: `${cluster.count}`,
                }).element,
              });
            },
          },
        });
      }
    },
    // Ignore the warning about the missing `handleMarkerClicked` dependency;
    // adding this dependency will break the functionality.

    [mapIsReady, mapRef, plottedBagObjects],
  );

  React.useEffect(() => {
    /**
     * Draw the focused BAG object's parcel polygon on the map.
     * Fetched on demand (polygons are not in the list payload); a 404 simply
     * means the object has no parcel. Removed on deselect/switch/unmount.
     */
    perceelPolygonRef.current?.setMap(null);
    perceelPolygonRef.current = null;
    if (!focusedBagObject || !mapIsReady || !mapRef.current) {
      return;
    }
    let cancelled = false;
    let drawn = false;
    let idleListener: google.maps.MapsEventListener | null = null;
    let drawTimeout: ReturnType<typeof setTimeout> | null = null;
    getBagObjectPerceel(focusedBagObject.id)
      .then((perceel: IPerceelDetail) => {
        if (cancelled || !mapRef.current) {
          return;
        }
        const draw = () => {
          if (drawn || cancelled || !mapRef.current) {
            return;
          }
          drawn = true;
          perceelPolygonRef.current = new google.maps.Polygon({
            map: mapRef.current,
            paths: perceel.polygon.coordinates.map((ring: number[][]) =>
              ring.map(([lng, lat]: number[]) => ({ lat, lng })),
            ),
            strokeColor: "#1a73e8",
            strokeWeight: 2,
            fillColor: "#1a73e8",
            fillOpacity: 0.12,
            clickable: false,
          });
        };
        // Selecting an object also pans/zooms the map; inserting the overlay
        // mid-animation causes a visible stutter. Draw on the map's next
        // "idle" event (= animation finished). The timeout is a fallback for
        // selections that don't move the map, since those fire no idle event.
        idleListener = google.maps.event.addListenerOnce(
          mapRef.current,
          "idle",
          draw,
        );
        drawTimeout = setTimeout(draw, 600);
      })
      .catch(() => {
        // 404 (no parcel) or transient error: no overlay, no error message.
      });
    return () => {
      cancelled = true;
      idleListener?.remove();
      if (drawTimeout !== null) {
        clearTimeout(drawTimeout);
      }
      perceelPolygonRef.current?.setMap(null);
      perceelPolygonRef.current = null;
    };
  }, [focusedBagObject, mapIsReady]);

  const defaultBagObjectColDef: ColDef = {
    /**
     * Default column definition for the BAG objects grid (i.e. the master/main grid).
     */
    sortable: true,
    filter: true,
    filterParams: {
      buttons: ["apply", "reset"],
      maxNumConditions: 1,
    },
  };

  const bagObjectColDefs: ColGroupDef[] = [
    /**
     * Column definitions for the BAG objects grid (i.e. the master/main grid).
     *
     * Also includes column definitions for the organizations grid (i.e. the detail/nested grid),
     * which are used for filtering organizations in the detail grid, and are hidden to
     * avoid them being displayed in the main grid.
     */
    {
      headerName: "Object",
      children: [
        {
          field: "addressFormatted",
          headerName: "Adres",
          cellDataType: "text",
          filter: "agTextColumnFilter",
          cellRenderer: "agGroupCellRenderer",
          flex: 6,
        },
        {
          field: "floorArea",
          headerName: "Oppervlakte (m²)",
          cellDataType: "number",
          filter: "agNumberColumnFilter",
          sortable: true,
          flex: 2,
          valueFormatter: (params) => params.value.toLocaleString("nl-NL"),
        },
        {
          field: "yearOfConstruction",
          headerName: "Bouwjaar",
          cellDataType: "number",
          filter: "agNumberColumnFilter",
          // Hidden field for filtering purposes.
          hide: true,
        },
        {
          field: "functions",
          headerName: "Gebruiksdoelen",

          filter: "agSetColumnFilter",
          cellDataType: "text",
          // Hidden field for filtering purposes.
          hide: true,
        },
        {
          field: "status",
          headerName: "Status",

          filter: "agSetColumnFilter",
          cellDataType: "text",
          // Hidden field for filtering purposes.
          hide: true,
        },
        {
          field: "buildingStatus",
          headerName: "Pand status",

          filter: "agSetColumnFilter",
          cellDataType: "text",
          // Hidden field for filtering purposes.
          hide: true,
        },
        {
          field: "mainOrganizationName",
          headerName: "Hoofdbedrijf",
          cellDataType: "text",
          flex: 4,
        },
        {
          field: "organizationsCount",
          headerName: "Aantal bedrijven actief",
          cellDataType: "number",
          filter: "agNumberColumnFilter",
          // Hidden field for filtering purposes.
          hide: true,
        },
        {
          field: "notes",
          headerName: "Notities ingevuld",
          valueGetter: (params) => (params.data.appData?.notes ? "Ja" : "Nee"),
          filter: "agSetColumnFilter",
          cellDataType: "text",
          // Hidden field for filtering purposes.
          hide: true,
        },
        {
          field: "clearHeight",
          headerName: "Vrije hoogte (m)",
          valueGetter: (params) => params.data.appData?.clearHeight,
          filter: "agNumberColumnFilter",
          cellDataType: "number",
          // Hidden field for filtering purposes.
          hide: true,
        },
        {
          field: "officeArea",
          headerName: "Kantooroppervlakte (m²)",
          valueGetter: (params) => params.data.appData?.officeArea,
          filter: "agNumberColumnFilter",
          cellDataType: "number",
          // Hidden field for filtering purposes.
          hide: true,
        },
        {
          field: "maxFloorLoad",
          headerName: "Maximale vloerbelasting (kg/m²)",
          valueGetter: (params) => params.data.appData?.maxFloorLoad,
          filter: "agNumberColumnFilter",
          cellDataType: "number",
          // Hidden field for filtering purposes.
          hide: true,
        },
        {
          field: "lease",
          headerName: "Contract ingevuld",
          valueGetter: (params) => (params.data.lease ? "Ja" : "Nee"),
          filter: "agSetColumnFilter",
          cellDataType: "text",
          // Hidden field for filtering purposes.
          hide: true,
        },
        {
          field: "pricePerM2PerYear",
          headerName: "Huurprijs (€/m²/jaar)",
          valueGetter: (params) => params.data.lease?.pricePerM2PerYear,
          filter: "agNumberColumnFilter",
          cellDataType: "number",
          // Hidden field for filtering purposes.
          hide: true,
        },
      ],
    },
    {
      headerName: "Bedrijf",
      // Column definitions for the organizations grid (i.e. the detail/nested grid),
      // which are used for filtering organizations in the detail grid, and are hidden to
      // avoid them being displayed in the main grid.
      children: [
        {
          field: "name",
          headerName: "Bedrijfsnaam",
          valueGetter: (params) =>
            params.data.organizations
              ?.flatMap((_organization: IOrganization) => _organization.name)
              .join(", ") || "",
          hide: true,
          filter: "agTextColumnFilter",
          filterParams: { filterOptions: ["contains"] },
        },
        {
          field: "foundationYear",
          headerName: "Oprichtingsjaar",
          valueGetter: (params) =>
            params.data.organizations?.flatMap((_organization: IOrganization) =>
              _organization.bedrijfsdata.flatMap(
                (_bedrijfsdata: IBedrijfsdataObject) =>
                  _bedrijfsdata.foundationYear || null,
              ),
            ) || [],
          hide: true,
          filter: "agSetColumnFilter",
        },
        {
          field: "numberOfEmployees",
          headerName: "Aantal medewerkers (vestiging)",
          valueGetter: (params) =>
            params.data.organizations?.flatMap((_organization: IOrganization) =>
              _organization.bedrijfsdata.flatMap(
                (_bedrijfsdata: IBedrijfsdataObject) =>
                  _bedrijfsdata.numberOfEmployees || null,
              ),
            ) || [],
          hide: true,
          filter: "agSetColumnFilter",
        },
        {
          field: "organizationType",
          headerName: "Rechtsvorm",
          valueGetter: (params) =>
            params.data.organizations?.flatMap((_organization: IOrganization) =>
              _organization.bedrijfsdata.flatMap(
                (_bedrijfsdata: IBedrijfsdataObject) =>
                  _bedrijfsdata.organizationType || null,
              ),
            ) || [],
          hide: true,
          filter: "agSetColumnFilter",
        },
        {
          field: "locationType",
          headerName: "Vestiging",
          valueGetter: (params) =>
            params.data.organizations?.flatMap((_organization: IOrganization) =>
              _organization.bedrijfsdata.flatMap(
                (_bedrijfsdata: IBedrijfsdataObject) =>
                  _bedrijfsdata.locationType || null,
              ),
            ) || [],
          hide: true,
          filter: "agSetColumnFilter",
        },
        {
          field: "sbiCodes",
          headerName: "SBI codes",
          valueGetter: (params) =>
            params.data.organizations?.flatMap((_organization: IOrganization) =>
              _organization.bedrijfsdata.flatMap(
                (_bedrijfsdata: IBedrijfsdataObject) =>
                  _bedrijfsdata.sbiCodes.flatMap(
                    (_sbiCode) => _sbiCode || null,
                  ) || null,
              ),
            ) || [],
          hide: true,
          filter: "agSetColumnFilter",
        },
        {
          field: "kvkBranches",
          headerName: "Branches",
          valueGetter: (params) =>
            params.data.organizations?.flatMap((_organization: IOrganization) =>
              _organization.bedrijfsdata.flatMap(
                (_bedrijfsdata: IBedrijfsdataObject) =>
                  _bedrijfsdata.kvkBranches.flatMap(
                    (_branche) => _branche || null,
                  ) || null,
              ),
            ) || [],
          hide: true,
          filter: "agSetColumnFilter",
        },
        {
          field: "notes",
          headerName: "Notities ingevuld",
          valueGetter: (params) =>
            (
              params.data.organizations?.flatMap(
                (_organization: IOrganization) =>
                  _organization.appData?.notes ? "Ja" : "Nee",
              ) || []
            ).includes("Ja")
              ? "Ja"
              : "Nee",
          filter: "agSetColumnFilter",
          cellDataType: "text",
          hide: true,
        },
        {
          field: "lastContacted",
          headerName: "Contact opgenomen",
          valueGetter: (params) =>
            (
              params.data.organizations?.flatMap(
                (_organization: IOrganization) =>
                  _organization.appData?.lastContacted ? "Ja" : "Nee",
              ) || []
            ).includes("Ja")
              ? "Ja"
              : "Nee",
          filter: "agSetColumnFilter",
          cellDataType: "text",
          hide: true,
        },
      ],
    },
    {
      headerName: "Perceel",
      children: [
        {
          field: "perceelOppervlakte",
          headerName: "Perceel (m²)",
          cellDataType: "number",
          filter: "agNumberColumnFilter",
          sortable: true,
          flex: 2,
          valueFormatter: (params) =>
            params.value != null ? params.value.toLocaleString("nl-NL") : "",
        },
        {
          field: "kadastraleAanduiding",
          headerName: "Kadastrale aanduiding",
          cellDataType: "text",
          filter: "agTextColumnFilter",
          // Hidden field for filtering purposes.
          hide: true,
        },
      ],
    },
  ];

  const refreshPlottedBagObjects = () => {
    /**
     * Refresh the plotted BAG objects on the map based on the active filters.
     */
    const gridApi = gridRef.current?.api;
    if (gridApi) {
      const filteredRows: IBagObject[] = [];
      for (let i = 0; i < gridApi.getDisplayedRowCount(); i++) {
        const rowNode = gridApi.getDisplayedRowAtIndex(i);
        if (rowNode?.data) filteredRows.push(rowNode.data);
      }
      setPlottedBagObjects(filteredRows);
    }
  };

  const restoreFocusedRow = () => {
    /**
     * Restore the focused row in the BAG objects grid after filtering or sorting.
     */
    if (focusedBagObject) focusOnRow(focusedBagObject, true);
  };

  const handleFilterChanged = () => {
    /**
     * Handle filter and ordering changes in the BAG objects grid.
     */
    refreshPlottedBagObjects();
    restoreFocusedRow();
  };

  const handleSelectionChanged = () => {
    /**
     * Handle selection changes in the BAG objects grid.
     */
    setSelectedBagObjects(
      bagObjects.filter((_bagObject) =>
        gridRef.current?.api
          .getSelectedNodes()
          .some((node) => node.data.id === _bagObject.id),
      ),
    );
  };

  const handleExportSelection = () => {
    /**
     * Export the selected BAG objects to an Excel file.
     */
    if (leadSearch && selectedBagObjects.length > 0) {
      setIsExportingSelection(true);
      generateExcelForSelection(leadSearch, selectedBagObjects).then(() =>
        setIsExportingSelection(false),
      );
    }
  };

  return (
    <Container fluid={true} className="w-100 min-h-100vh d-flex flex-column">
      <NavBar isLoading={isLoading} previousPath={routes.root} />
      {isLoading ? (
        <LoadingIndicator />
      ) : (
        <div className="d-flex flex-column flex-grow-1 justify-content-between align-items-center mt-3">
          <div className="d-flex flex-column flex-grow-1 w-100">
            <div className="d-flex justify-content-between">
              <h3 className="text-start mb-3 text-truncate">
                Zoekopdracht &quot;{leadSearch?.title}&quot; (
                {leadSearch?.dateFormatted})
              </h3>

              <div>
                <Button
                  title="Exporteer selectie"
                  type="button"
                  variant="primary"
                  className="px-4 text-nowrap"
                  disabled={
                    selectedBagObjects.length === 0 || isExportingSelection
                  }
                  onClick={handleExportSelection}
                >
                  {isExportingSelection
                    ? "Bezig met exporteren..."
                    : "Exporteer selectie"}
                </Button>
              </div>
            </div>
            <Row className="flex-grow-1 mb-3">
              <Col>
                <Map
                  onTilesLoaded={(event: MapEvent) => {
                    if (event.map) {
                      mapRef.current = event.map;
                      setMapIsReady(true);
                    }
                  }}
                  mapId="map-lead-search-detail"
                  defaultCenter={{
                    lat: mapDefaultCenter.coordinates[1],
                    lng: mapDefaultCenter.coordinates[0],
                  }}
                  defaultZoom={mapDefaultZoom}
                  minZoom={6}
                  gestureHandling="greedy"
                  controlSize={32}
                  mapTypeId={google.maps.MapTypeId.ROADMAP}
                />
              </Col>

              <Col>
                <AgGridReact
                  ref={gridRef}
                  theme={themeBalham}
                  animateRows={false}
                  masterDetail={true}
                  isRowMaster={() => true}
                  keepDetailRows={true}
                  detailRowAutoHeight={true}
                  columnDefs={bagObjectColDefs}
                  defaultColDef={defaultBagObjectColDef}
                  rowData={bagObjects}
                  getRowId={(params) => params.data.id}
                  components={{ leadDetail: LeadDetailCell }}
                  detailCellRenderer="leadDetail"
                  // Add context to detail cell renderer.
                  detailCellRendererParams={{ leadSearchId: leadSearchId }}
                  rowSelection={{ mode: "multiRow", selectAll: "filtered" }}
                  onSelectionChanged={handleSelectionChanged}
                  onGridReady={refreshPlottedBagObjects}
                  onFilterChanged={handleFilterChanged}
                  onRowClicked={handleRowClicked}
                  onRowGroupOpened={handleRowGroupOpened}
                  onSortChanged={restoreFocusedRow}
                  localeText={AG_GRID_LOCALE_NL}
                  sideBar={{
                    toolPanels: [
                      {
                        id: "filters",
                        labelDefault: "Filters",
                        labelKey: "filters",
                        iconKey: "filter",
                        toolPanel: "agFiltersToolPanel",
                        toolPanelParams: {
                          suppressFilterSearch: true,
                        },
                      },
                    ],
                    defaultToolPanel: undefined,
                  }}
                  alwaysShowVerticalScroll={true}
                />
              </Col>
            </Row>
          </div>
        </div>
      )}
    </Container>
  );
}
